import React, { FC, useEffect } from 'react';
import { DatePickerInput, DatePickerInputLocale, TextFieldTheme, SectionNotification } from 'wix-ui-tpa/cssVars';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useStyles } from '@wix/tpa-settings/react';
import { Error as ErrorIcon } from '@wix/wix-ui-icons-common/on-stage';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useLocale } from '../../../../hooks/useLocale';
import componentStyles, { FormFieldBorderStyle } from '../../stylesParams';
import { isDateInRange } from '../../utils/date-constraints';
import { st, classes } from './PlanStartDatePicker.st.css';

interface PlanStartDatePickerProps {
  value: Date | undefined;
  onChange: (value: Date) => void;
  className?: string;
  min?: string;
  max?: string;
  disableAllDates?: boolean;
  noAvailableDatesMessage?: string;
  onLoad?: () => void;
}

const PlanStartDatePicker: FC<PlanStartDatePickerProps> = ({
  value,
  onChange,
  className,
  min,
  max,
  disableAllDates,
  noAvailableDatesMessage,
  onLoad,
}) => {
  const styles = useStyles();
  const { locale } = useLocale();
  const { t } = useTranslation();

  useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  return (
    <>
      <DatePickerInput
        className={st(
          classes.root,
          {
            'no-border': styles.get(componentStyles.planFormFieldBorderStyle) === FormFieldBorderStyle.None,
            'bottom-border': styles.get(componentStyles.planFormFieldBorderStyle) === FormFieldBorderStyle.Bottom,
          },
          className,
        )}
        inputTheme={
          styles.get(componentStyles.planFormFieldBorderStyle) === FormFieldBorderStyle.Bottom
            ? TextFieldTheme.Line
            : TextFieldTheme.Box
        }
        value={value}
        onChange={(_value) => onChange(new Date(_value))}
        label={t('plan-customization.widget.datepicker-label')}
        excludePastDates
        removeClearButton
        locale={locale as DatePickerInputLocale}
        data-hook={VIEWER_HOOKS.PLAN_CUSTOMIZATION_DATE_PICKER}
        disabled={disableAllDates}
        filterDate={
          disableAllDates || min || max
            ? (date) => {
                if (disableAllDates) {
                  return false;
                }
                return isDateInRange(date, min, max);
              }
            : undefined
        }
      />
      {disableAllDates && (
        <SectionNotification
          type="error"
          size="compact"
          data-hook={VIEWER_HOOKS.PLAN_CUSTOMIZATION_DATE_PICKER_NOTIFICATION}
        >
          <SectionNotification.Icon icon={<ErrorIcon />} />
          <SectionNotification.Text>
            {noAvailableDatesMessage || t('plan-customization.widget.datepicker-no-available-dates')}
          </SectionNotification.Text>
        </SectionNotification>
      )}
    </>
  );
};

export default PlanStartDatePicker;
